import { renderSlot as _renderSlot, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, Transition as _Transition, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "flex flex-wrap gap-2 mb-6" }
const _hoisted_2 = { class: "text-xs" }
const _hoisted_3 = { class: "text-lg font-semibold" }
const _hoisted_4 = { class: "text-xs" }
const _hoisted_5 = {
  key: 0,
  class: "w-24 h-20 border-2 border-dashed border-gray-400 rounded-lg"
}
const _hoisted_6 = { class: "flex items-start gap-2" }
const _hoisted_7 = { class: "px-6 md:px-8 pt-6" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-end sm:flex-wrap" }
const _hoisted_10 = { class: "flex flex-wrap items-center gap-2 sm:contents" }
const _hoisted_11 = { class: "truncate" }
const _hoisted_12 = { class: "truncate" }
const _hoisted_13 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = { class: "p-3 cursor-pointer" }
const _hoisted_17 = ["value"]
const _hoisted_18 = ["src"]
const _hoisted_19 = {
  key: 1,
  class: "h-8 w-8 mr-3 rounded bg-gray-100 flex-shrink-0"
}
const _hoisted_20 = { class: "font-semibold text-gray-700 truncate" }
const _hoisted_21 = {
  key: 0,
  "data-test": "franchise-lock",
  class: "ml-2 inline-flex shrink-0 items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-600"
}
const _hoisted_22 = { class: "sr-only" }
const _hoisted_23 = {
  key: 0,
  class: "truncate text-gray-500"
}
const _hoisted_24 = {
  key: 0,
  class: "text-center pt-3 pb-4"
}

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { ChevronDownIcon, FunnelIcon, LockClosedIcon, MagnifyingGlassIcon, XCircleIcon } from '@heroicons/vue/20/solid'
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import _ from 'lodash'
import { DatePicker } from 'v-calendar'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import ImportWizardModal from '@/components/neue/ImportWizardModal.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import { useToast } from '@/composables/useToast'
import { dateObjectToServerDay, serverDayToDateObject } from '@/helpers/dates'
import { franchiseRecordLockText, isFranchiseManaged } from '@/helpers/franchiseManaged'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'

// compactHeader: a page that adds its own controls to the header leaves Import CSV to wider screens.
const defaultNewProductType = 'FoodProduct'

const pageSize = 25

export default {
  __name: 'ProductList',
  props: { compactHeader: { type: Boolean, default: false } },
  setup(__props) {



const mobileMediaQuery = window.matchMedia('(max-width: 639px)')
const isMobile = ref(mobileMediaQuery.matches)
const onMobileChange = e => {
  isMobile.value = e.matches
}
onMounted(() => mobileMediaQuery.addEventListener('change', onMobileChange))
onUnmounted(() => mobileMediaQuery.removeEventListener('change', onMobileChange))

const productCols = computed(() =>
  isMobile.value ? [{ width: 'minmax(80px, 1fr)' }] : [{ width: 'minmax(80px, 2fr)' }, { width: 'minmax(120px, 3fr)' }]
)

const router = useRouter()
const loading = ref(true)
const products = ref([])
const query = ref('')
const searchResults = ref([])
const searching = ref(false)
const currentFolderId = ref(null)
const cursor = ref(null)
const size = ref(null)
const page = ref(0)
const folderModal = ref({
  open: false,
  targetId: null
})
const storeModal = ref({
  open: false,
  storeId: null,
  categoryId: null,
  alwaysAvailable: true,
  availabilityDays: []
})
const newProductModal = ref({
  open: false,
  type: defaultNewProductType
})
const importModalOpen = ref(false)
const selectedProducts = ref([])
const selectedDay = ref(null)
const sortBy = ref(null)
const sortDirection = ref(null)

const stores = computed(() => mapType('store'))
const targetStore = computed(() => mapRelationship({ type: 'store', id: storeModal.value.storeId }))
const categories = store =>
  mapRelationships(store.relationships.categories.data)
    .filter(x => x.attributes.type !== 'UpsellCategory')
    .sort((a, b) => a.attributes.order_value - b.attributes.order_value)
const folders = computed(() => mapType('folder'))
const currentFolder = computed(() => mapRelationship({ type: 'folder', id: currentFolderId.value }))
const items = folder => mapRelationships(folder.relationships.folder_items.data)
const folderProducts = folder =>
  items(folder)
    .map(x =>
      x.attributes.belongs_to_class === 'Product'
        ? mapRelationship({ type: 'product', id: x.attributes.belongs_to_id })
        : null
    )
    .filter(Boolean)

const ensureProducts = async folder => {
  const missingIds = items(folder)
    .filter(item => item.attributes.belongs_to_class === 'Product')
    .map(item => item.attributes.belongs_to_id)
    .filter(id => !mapRelationship({ type: 'product', id }))

  if (!missingIds.length) return

  loading.value = true

  const params = new URLSearchParams()
  params.append('type', 'product')
  for (const id of missingIds) {
    params.append('ids[]', id)
  }
  params.append('included', 'attachments')

  return api.call('GET', 'relationships?' + params).finally(() => {
    loading.value = false
  })
}

watch(currentFolderId, () => {
  if (!currentFolderId.value) return
  ensureProducts(currentFolder.value)
  selectedProducts.value = selectedProducts.value.filter(id =>
    folderProducts(currentFolder.value).some(product => product.id === id)
  )
})

const addToFolder = () => {
  if (!folderModal.value.targetId) return

  const targetFolder = folders.value.find(x => x.id === folderModal.value.targetId)
  const existingIds = items(targetFolder).map(x => String(x.attributes.belongs_to_id))

  api.call('PUT', `folders/${folderModal.value.targetId}?included=folder_items`, {
    folder: {
      folder_items_attributes: selectedProducts.value
        .filter(x => !existingIds.includes(x))
        .map(x => ({
          belongs_to_class: 'Product',
          belongs_to_id: x
        }))
    }
  })

  folderModal.value.open = false
  selectedProducts.value = []
}

const removeFromFolder = () => {
  api
    .call('PATCH', `folders/${currentFolderId.value}?included=folder_items`, {
      folder: {
        folder_items_attributes: items(currentFolder.value)
          .filter(item => selectedProducts.value.includes(String(item.attributes.belongs_to_id)))
          .map(item => ({ id: item.id, _destroy: true }))
      }
    })
    .then(() => {
      selectedProducts.value = []
    })
}

const addToStore = () => {
  for (const productId of selectedProducts.value) {
    const existingPair = mapRelationships(
      mapRelationship({ type: 'category', id: storeModal.value.categoryId }).relationships.category_product_store_pairs
        .data
    ).find(x => x.attributes.product_id == productId)

    if (existingPair && !storeModal.value.alwaysAvailable) {
      api.call(
        'PUT',
        `stores/${storeModal.value.storeId}/category_product_store_pairs/${existingPair.id}?included=fulfillment_availabilities`,
        {
          category_product_store_pair: {
            fulfillment_availabilities_attributes: storeModal.value.availabilityDays.map(date => ({ date }))
          }
        }
      )
    } else {
      api.call('POST', `stores/${storeModal.value.storeId}/category_product_store_pairs`, {
        category_product_store_pair: {
          category_id: storeModal.value.categoryId,
          product_id: productId,
          ...(!storeModal.value.alwaysAvailable && {
            available_for_all_fulfillment_availabilities: false,
            fulfillment_availabilities_attributes: storeModal.value.availabilityDays.map(date => ({ date }))
          })
        }
      })
    }
  }

  storeModal.value.open = false
  storeModal.value.availabilityDays = []
}

const loadProducts = () => {
  loading.value = true
  page.value = 0
  cursor.value = null

  return api.call('GET', `products?included=attachments&offset=${pageSize}&cursor=0`).then(x => {
    products.value = [x.data.data]
    loading.value = false
    cursor.value = x.data.next_cursor
    size.value = x.data.total_records
  })
}

loadProducts()
api.call('GET', 'folders?included=folder_items')
api.call('GET', 'stores?included=categories')

const view = computed(() => {
  if (currentFolderId.value) {
    return folderProducts(currentFolder.value)
  }
  return query.value ? searchResults.value : products.value[page.value] || []
})

const search = () => {
  if (!query.value) {
    searchResults.value = []

    page.value = 0
    products.value = []
    cursor.value = null
    loading.value = true

    let apiUrl = `products?included=attachments&offset=${pageSize}&cursor=0`
    if (sortBy.value && sortDirection.value) {
      apiUrl += `&sort_by_field=${sortBy.value}&sort_direction=${sortDirection.value}`
    }

    api.call('GET', apiUrl).then(x => {
      products.value.push(x.data.data)
      loading.value = false
      cursor.value = x.data.next_cursor
      size.value = x.data.total_records
    })
    return
  }
  searching.value = true
  performSearch()
}

const performSearch = _.debounce(
  () => {
    if (!query.value) return
    api
      .call('POST', 'products/search?included=attachments&no_limit=true', { search: { text: query.value } })
      .then(x => {
        searchResults.value = x.data.data
        if (sortBy.value && sortDirection.value) {
          searchResults.value.sort((a, b) => {
            const aValue = a.attributes.product_name || ''
            const bValue = b.attributes.product_name || ''

            if (sortDirection.value === 'asc') {
              return aValue.localeCompare(bValue)
            } else {
              return bValue.localeCompare(aValue)
            }
          })
        }

        searching.value = false
      })
  },
  200,
  { maxWait: 1000 }
)

const prev = ret => {
  page.value--
  ret()
}

const next = ret => {
  if (products.value.length <= page.value + 1 && cursor.value) {
    loading.value = true

    let apiUrl = `products?included=attachments&offset=${pageSize}&cursor=${cursor.value}&direction=next`
    if (sortBy.value && sortDirection.value) {
      apiUrl += `&sort_by_field=${sortBy.value}&sort_direction=${sortDirection.value}`
    }

    api.call('GET', apiUrl).then(x => {
      products.value.push(x.data.data)
      cursor.value = x.data.next_cursor
      page.value++
      loading.value = false
      ret()
    })
  } else {
    page.value++
    ret()
  }
}

const image = product => mapRelationships(product.relationships.attachments.data)[0]?.attributes.secure_url

const openNewProductModal = () => {
  newProductModal.value.type = defaultNewProductType
  newProductModal.value.open = true
}

const create = () => {
  newProductModal.value.open = false
  api
    .call('POST', 'products', {
      product: {
        product_name: 'New product',
        type: newProductModal.value.type,
        default_price_cents: 0
      }
    })
    .then(x => router.push('/store/products/' + x.data.data.id))
}

const toggleDay = day => {
  if (storeModal.value.availabilityDays.includes(day)) {
    storeModal.value.availabilityDays = storeModal.value.availabilityDays.filter(x => x !== day)
  } else {
    storeModal.value.availabilityDays.push(day)
  }
}

const updateSort = (field, direction) => {
  sortBy.value = field
  sortDirection.value = direction

  if (query.value) {
    if (field && direction) {
      searchResults.value.sort((a, b) => {
        const aValue = a.attributes.product_name || ''
        const bValue = b.attributes.product_name || ''

        if (direction === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      })
    }
    return
  }

  page.value = 0
  cursor.value = null

  loading.value = true

  let apiUrl = `products?included=attachments&offset=${pageSize}&cursor=0`
  if (field && direction) {
    apiUrl += `&sort_by_field=${field}&sort_direction=${direction}`
  }

  api.call('GET', apiUrl).then(x => {
    products.value = [x.data.data]
    loading.value = false
    cursor.value = x.data.next_cursor
    size.value = x.data.total_records
  })
}

const { showToast } = useToast()

// Every product record this page has loaded, so a selection made on another page or in a search still resolves.
const loadedProduct = id =>
  [...products.value.flat(), ...searchResults.value, ...view.value].find(product => product.id === id)

// The API refuses to delete a product the franchise manages, so the bulk delete leaves those out and says so.
const toastSkippedManaged = managed => {
  const franchiseName = managed[0].attributes.franchise_name || 'the franchise'
  const noun = managed.length === 1 ? 'product' : 'products'
  showToast(`Skipped ${managed.length} ${noun} managed by ${franchiseName}.`, { type: 'info' })
}

const deleteSelectedProducts = () => {
  const managed = selectedProducts.value.map(loadedProduct).filter(isFranchiseManaged)
  const deletable = selectedProducts.value.filter(id => !managed.some(product => product.id === id))
  if (managed.length) toastSkippedManaged(managed)
  if (!deletable.length) {
    selectedProducts.value = []
    return
  }

  const deletePromises = deletable.map(productId => api.call('DELETE', `products/${productId}`))

  Promise.all(deletePromises)
    .then(() => {
      selectedProducts.value = []

      if (currentFolderId.value) {
        api.call('GET', 'folders?included=folder_items')
      } else {
        loading.value = true

        let apiUrl = `products?included=attachments&offset=${pageSize}&cursor=0`
        if (sortBy.value && sortDirection.value) {
          apiUrl += `&sort_by_field=${sortBy.value}&sort_direction=${sortDirection.value}`
        }

        api.call('GET', apiUrl).then(x => {
          products.value = [x.data.data]
          cursor.value = x.data.next_cursor
          size.value = x.data.total_records
          loading.value = false
        })
      }
    })
    .catch(error => {
      console.error('Error deleting products:', error)
    })
}

return (_ctx, _cache) => {
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_SelectInput = _resolveComponent("SelectInput")
  const _component_FormElement = _resolveComponent("FormElement")
  const _component_FormSection = _resolveComponent("FormSection")
  const _component_TheModal = _resolveComponent("TheModal")
  const _component_NeueToggle = _resolveComponent("NeueToggle")
  const _component_BasicSpinner = _resolveComponent("BasicSpinner")
  const _component_TablistHeader = _resolveComponent("TablistHeader")
  const _component_router_link = _resolveComponent("router-link")
  const _component_TablistRow = _resolveComponent("TablistRow")
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_NeueTablist = _resolveComponent("NeueTablist")
  const _component_NeuePagination = _resolveComponent("NeuePagination")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")
  const _directive_tippy = _resolveDirective("tippy")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, { text: "Product library" }, {
        right: _withCtx(() => [
          _renderSlot(_ctx.$slots, "right"),
          _createVNode(_component_NeueButton, {
            type: "secondary",
            class: _normalizeClass(__props.compactHeader && 'hidden sm:inline-flex'),
            onClick: _cache[0] || (_cache[0] = $event => (importModalOpen.value = true))
          }, {
            default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
              _createTextVNode(" Import CSV ", -1)
            ]))]),
            _: 1
          }, 8, ["class"]),
          _createVNode(_component_NeueButton, {
            type: "primary",
            onClick: openNewProductModal
          }, {
            default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
              _createTextVNode("New", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 3
      })
    ]),
    main: _withCtx(() => [
      _createVNode(ImportWizardModal, {
        "is-open": importModalOpen.value,
        "import-type": "ProductsImport",
        onClose: _cache[1] || (_cache[1] = $event => (importModalOpen.value = false)),
        onCompleted: loadProducts
      }, null, 8, ["is-open"]),
      _createVNode(_component_TheModal, {
        "is-open": newProductModal.value.open,
        "modal-title": "New product",
        onCloseModal: _cache[4] || (_cache[4] = $event => (newProductModal.value.open = false))
      }, {
        main: _withCtx(() => [
          _createVNode(_component_FormSection, null, {
            default: _withCtx(() => [
              _createVNode(_component_FormElement, null, {
                default: _withCtx(() => [
                  _createVNode(_component_SelectInput, {
                    modelValue: newProductModal.value.type,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((newProductModal.value.type) = $event)),
                    options: [
                  { value: 'FoodProduct', name: 'Food Product' },
                  { value: 'GiftCardProduct', name: 'Gift Card' },
                  { value: 'Product', name: 'Non-Food Product' }
                ],
                    name: "Product type"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        footer: _withCtx(() => [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: _cache[3] || (_cache[3] = $event => (newProductModal.value.open = false))
          }, {
            default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_NeueButton, {
            type: newProductModal.value.type ? 'primary' : 'inactive',
            onClick: create
          }, {
            default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
              _createTextVNode("Add", -1)
            ]))]),
            _: 1
          }, 8, ["type"])
        ]),
        _: 1
      }, 8, ["is-open"]),
      _createVNode(_component_TheModal, {
        "is-open": folderModal.value.open,
        "modal-title": "Add products to folder",
        onCloseModal: _cache[7] || (_cache[7] = $event => (folderModal.value.open = false))
      }, {
        main: _withCtx(() => [
          _createVNode(_component_FormSection, null, {
            default: _withCtx(() => [
              _createVNode(_component_FormElement, null, {
                default: _withCtx(() => [
                  _createVNode(_component_SelectInput, {
                    modelValue: folderModal.value.targetId,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => ((folderModal.value.targetId) = $event)),
                    options: 
                  folders.value.map(folder => ({
                    value: folder.id,
                    name: folder.attributes.name
                  }))
                ,
                    placeholder: "Select folder"
                  }, null, 8, ["modelValue", "options"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        footer: _withCtx(() => [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: _cache[6] || (_cache[6] = $event => (folderModal.value.open = false))
          }, {
            default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_NeueButton, {
            type: folderModal.value.targetId ? 'primary' : 'inactive',
            onClick: addToFolder
          }, {
            default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
              _createTextVNode("Add", -1)
            ]))]),
            _: 1
          }, 8, ["type"])
        ]),
        _: 1
      }, 8, ["is-open"]),
      _createVNode(_component_TheModal, {
        "is-open": storeModal.value.open,
        "modal-title": "Add products to store",
        onCloseModal: _cache[14] || (_cache[14] = $event => (storeModal.value.open = false))
      }, {
        main: _withCtx(() => [
          _createVNode(_component_FormSection, null, {
            default: _withCtx(() => [
              _createVNode(_component_FormElement, null, {
                default: _withCtx(() => [
                  _createVNode(_component_SelectInput, {
                    modelValue: storeModal.value.storeId,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = $event => ((storeModal.value.storeId) = $event)),
                    options: stores.value.map(store => ({ value: store.id, name: store.attributes.store_name })),
                    placeholder: "Select store"
                  }, null, 8, ["modelValue", "options"])
                ]),
                _: 1
              }),
              (storeModal.value.storeId)
                ? (_openBlock(), _createBlock(_component_FormElement, { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_SelectInput, {
                        modelValue: storeModal.value.categoryId,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = $event => ((storeModal.value.categoryId) = $event)),
                        options: 
                  categories(targetStore.value).map(category => ({
                    value: category.id,
                    name: category.attributes.category_name
                  }))
                ,
                        placeholder: "Select category"
                      }, null, 8, ["modelValue", "options"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (storeModal.value.categoryId)
                ? (_openBlock(), _createBlock(_component_FormElement, { key: 1 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_NeueToggle, {
                        modelValue: storeModal.value.alwaysAvailable,
                        "onUpdate:modelValue": _cache[10] || (_cache[10] = $event => ((storeModal.value.alwaysAvailable) = $event)),
                        name: "Always available"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (!storeModal.value.alwaysAvailable)
                ? (_openBlock(), _createBlock(_component_FormElement, { key: 2 }, {
                    default: _withCtx(() => [
                      _cache[30] || (_cache[30] = _createElementVNode("div", { class: "mb-2 text-sm font-medium text-gray-900" }, "Selected availability days", -1)),
                      _createElementVNode("div", _hoisted_1, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList([...storeModal.value.availabilityDays].sort(), (day) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: day,
                            class: "w-24 h-20 leading-6 border-2 border-black rounded-lg flex flex-col items-center justify-center relative"
                          }, [
                            _createVNode(_unref(XCircleIcon), {
                              class: "absolute -top-2.5 -right-2.5 bg-white cursor-pointer h-5 w-5 hover:text-black",
                              onClick: $event => (toggleDay(day))
                            }, null, 8, ["onClick"]),
                            _createElementVNode("div", _hoisted_2, _toDisplayString(new Intl.DateTimeFormat([], { weekday: 'long' }).format(_unref(serverDayToDateObject)(day))), 1),
                            _createElementVNode("div", _hoisted_3, _toDisplayString(new Intl.DateTimeFormat([], { day: 'numeric' }).format(_unref(serverDayToDateObject)(day))), 1),
                            _createElementVNode("div", _hoisted_4, _toDisplayString(new Intl.DateTimeFormat([], { month: 'short' }).format(_unref(serverDayToDateObject)(day))), 1)
                          ]))
                        }), 128)),
                        (!storeModal.value.availabilityDays.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_5))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_unref(DatePicker), {
                          modelValue: selectedDay.value,
                          "onUpdate:modelValue": _cache[11] || (_cache[11] = $event => ((selectedDay).value = $event)),
                          mode: "single",
                          "min-date": new Date()
                        }, null, 8, ["modelValue", "min-date"]),
                        _createVNode(_component_NeueButton, {
                          type: 
                    !selectedDay.value || storeModal.value.availabilityDays.includes(_unref(dateObjectToServerDay)(selectedDay.value))
                      ? 'inactive'
                      : 'primary'
                  ,
                          onClick: _cache[12] || (_cache[12] = $event => (toggleDay(_unref(dateObjectToServerDay)(selectedDay.value))))
                        }, {
                          default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                            _createTextVNode(" Add ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["type"])
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        footer: _withCtx(() => [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: _cache[13] || (_cache[13] = $event => (storeModal.value.open = false))
          }, {
            default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_NeueButton, {
            type: storeModal.value.storeId && storeModal.value.categoryId ? 'primary' : 'inactive',
            onClick: addToStore
          }, {
            default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
              _createTextVNode(" Add ", -1)
            ]))]),
            _: 1
          }, 8, ["type"])
        ]),
        _: 1
      }, 8, ["is-open"]),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(_component_NeueLoading, { loading: loading.value }, {
          default: _withCtx(() => [
            (products.value.length || loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      (selectedProducts.value.length)
                        ? (_openBlock(), _createBlock(_unref(Popover), {
                            key: 0,
                            as: "div",
                            class: "relative"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(PopoverButton), { class: "inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500" }, {
                                default: _withCtx(() => [
                                  _cache[33] || (_cache[33] = _createElementVNode("span", { class: "truncate" }, "Actions", -1)),
                                  _createVNode(_unref(ChevronDownIcon), {
                                    class: "h-4 w-4 text-gray-500 shrink-0",
                                    "aria-hidden": "true"
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_Transition, {
                                "enter-active-class": "transition ease-out duration-100",
                                "enter-from-class": "transform opacity-0 scale-95",
                                "enter-to-class": "transform opacity-100 scale-100",
                                "leave-active-class": "transition ease-in duration-75",
                                "leave-from-class": "transform opacity-100 scale-100",
                                "leave-to-class": "transform opacity-0 scale-95"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(PopoverPanel), { class: "absolute left-0 sm:left-auto sm:right-0 z-20 mt-2 origin-top-left sm:origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 px-4 py-1" }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(PopoverButton), {
                                        class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                        onClick: _cache[15] || (_cache[15] = $event => (folderModal.value.open = true))
                                      }, {
                                        default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                                          _createTextVNode(" Add to folder ", -1)
                                        ]))]),
                                        _: 1
                                      }),
                                      (currentFolderId.value)
                                        ? (_openBlock(), _createBlock(_unref(PopoverButton), {
                                            key: 0,
                                            class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                            onClick: removeFromFolder
                                          }, {
                                            default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                                              _createTextVNode(" Remove from folder ", -1)
                                            ]))]),
                                            _: 1
                                          }))
                                        : _createCommentVNode("", true),
                                      _createVNode(_unref(PopoverButton), {
                                        class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                        onClick: _cache[16] || (_cache[16] = $event => (storeModal.value.open = true))
                                      }, {
                                        default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                                          _createTextVNode(" Add to store ", -1)
                                        ]))]),
                                        _: 1
                                      }),
                                      _createVNode(_unref(PopoverButton), {
                                        class: "text-sm font-medium whitespace-nowrap text-red-600 cursor-pointer my-3 block",
                                        onClick: deleteSelectedProducts
                                      }, {
                                        default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                                          _createTextVNode(" Delete products ", -1)
                                        ]))]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      _createVNode(_unref(Popover), {
                        as: "div",
                        class: "relative"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(PopoverButton), { class: "inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(FunnelIcon), { class: "h-4 w-4 text-gray-500 shrink-0" }),
                              _createElementVNode("span", _hoisted_11, _toDisplayString(sortBy.value
                          ? `Sort by product name ${sortDirection.value === 'asc' ? '(ascending)' : '(descending)'}`
                          : 'Sort by'), 1),
                              _createVNode(_unref(ChevronDownIcon), {
                                class: "h-4 w-4 text-gray-500 shrink-0",
                                "aria-hidden": "true"
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_Transition, {
                            "enter-active-class": "transition ease-out duration-100",
                            "enter-from-class": "transform opacity-0 scale-95",
                            "enter-to-class": "transform opacity-100 scale-100",
                            "leave-active-class": "transition ease-in duration-75",
                            "leave-from-class": "transform opacity-100 scale-100",
                            "leave-to-class": "transform opacity-0 scale-95"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(PopoverPanel), { class: "absolute left-0 sm:left-auto sm:right-0 z-20 mt-2 origin-top-left sm:origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 px-4 py-1" }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(PopoverButton), {
                                    class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                    onClick: _cache[17] || (_cache[17] = $event => (updateSort(null, null)))
                                  }, {
                                    default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                                      _createTextVNode(" None ", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_unref(PopoverButton), {
                                    class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                    onClick: _cache[18] || (_cache[18] = $event => (updateSort('product_name', 'asc')))
                                  }, {
                                    default: _withCtx(() => [...(_cache[39] || (_cache[39] = [
                                      _createTextVNode(" Product name (ascending) ", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_unref(PopoverButton), {
                                    class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                    onClick: _cache[19] || (_cache[19] = $event => (updateSort('product_name', 'desc')))
                                  }, {
                                    default: _withCtx(() => [...(_cache[40] || (_cache[40] = [
                                      _createTextVNode(" Product name (descending) ", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(Popover), {
                        as: "div",
                        class: "relative"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(PopoverButton), { class: "inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500" }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_12, _toDisplayString(currentFolder.value?.attributes.name ?? 'All products'), 1),
                              _createVNode(_unref(ChevronDownIcon), {
                                class: "h-4 w-4 text-gray-500 shrink-0",
                                "aria-hidden": "true"
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_Transition, {
                            "enter-active-class": "transition ease-out duration-100",
                            "enter-from-class": "transform opacity-0 scale-95",
                            "enter-to-class": "transform opacity-100 scale-100",
                            "leave-active-class": "transition ease-in duration-75",
                            "leave-from-class": "transform opacity-100 scale-100",
                            "leave-to-class": "transform opacity-0 scale-95"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(PopoverPanel), { class: "absolute left-0 sm:left-auto sm:right-0 z-20 mt-2 origin-top-left sm:origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 px-4 py-1" }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(PopoverButton), {
                                    class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                    onClick: _cache[20] || (_cache[20] = $event => (currentFolderId.value = null))
                                  }, {
                                    default: _withCtx(() => [...(_cache[41] || (_cache[41] = [
                                      _createTextVNode(" All products ", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(folders.value, (folder) => {
                                    return (_openBlock(), _createBlock(_unref(PopoverButton), {
                                      class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                      onClick: 
                          () => {
                            currentFolderId.value = folder.id
                            query.value = ''
                          }
                        
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(folder.attributes.name), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["onClick"]))
                                  }), 256))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _createElementVNode("div", {
                      class: _normalizeClass(["order-first sm:order-none w-full sm:w-56 relative flex items-center", { 'opacity-25 pointer-events-none': currentFolderId.value }])
                    }, [
                      (!searching.value)
                        ? (_openBlock(), _createBlock(_unref(MagnifyingGlassIcon), {
                            key: 0,
                            class: "pointer-events-none absolute left-3 h-4 w-4 text-gray-500"
                          }))
                        : (_openBlock(), _createBlock(_component_BasicSpinner, {
                            key: 1,
                            class: "pointer-events-none absolute left-3 h-4 w-4",
                            color: "black"
                          })),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[21] || (_cache[21] = $event => ((query).value = $event)),
                        class: "h-[34px] w-full pl-9 pr-3 rounded-full border border-gray-300 bg-white text-sm text-gray-700 placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500",
                        placeholder: "Search products...",
                        onInput: search
                      }, null, 544), [
                        [_vModelText, query.value]
                      ])
                    ], 2)
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    _createVNode(_component_NeueTablist, null, {
                      head: _withCtx(() => [
                        _createVNode(_component_TablistHeader, null, {
                          default: _withCtx(() => [
                            _createVNode(TablistGrid, {
                              columns: productCols.value,
                              header: ""
                            }, {
                              default: _withCtx(() => [
                                _cache[42] || (_cache[42] = _createElementVNode("div", null, "Name", -1)),
                                (!isMobile.value)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, "Description"))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 1
                            }, 8, ["columns"])
                          ]),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(view.value, (product) => {
                          return (_openBlock(), _createBlock(_component_TablistRow, {
                            key: product.id,
                            class: "!py-0 transition-colors"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, {
                                columns: productCols.value,
                                class: "items-center"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("div", _hoisted_15, [
                                    _createElementVNode("label", _hoisted_16, [
                                      _withDirectives(_createElementVNode("input", {
                                        "onUpdate:modelValue": _cache[22] || (_cache[22] = $event => ((selectedProducts).value = $event)),
                                        type: "checkbox",
                                        class: "h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-0 cursor-pointer",
                                        value: product.id
                                      }, null, 8, _hoisted_17), [
                                        [_vModelCheckbox, selectedProducts.value]
                                      ])
                                    ]),
                                    _createVNode(_component_router_link, {
                                      to: '/store/products/' + product.id,
                                      class: "flex items-center truncate"
                                    }, {
                                      default: _withCtx(() => [
                                        (image(product))
                                          ? (_openBlock(), _createElementBlock("img", {
                                              key: 0,
                                              class: "w-8 h-8 mr-3 rounded flex-shrink-0",
                                              src: image(product)
                                            }, null, 8, _hoisted_18))
                                          : (_openBlock(), _createElementBlock("div", _hoisted_19)),
                                        _createElementVNode("span", _hoisted_20, _toDisplayString(product.attributes.product_name), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["to"]),
                                    (_unref(isFranchiseManaged)(product))
                                      ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_21, [
                                          _createVNode(_unref(LockClosedIcon), {
                                            class: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                          }),
                                          _createElementVNode("span", _hoisted_22, _toDisplayString(_unref(franchiseRecordLockText)(product)), 1)
                                        ])), [
                                          [_directive_tippy, { content: _unref(franchiseRecordLockText)(product) }]
                                        ])
                                      : _createCommentVNode("", true)
                                  ]),
                                  (!isMobile.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                                        _createVNode(_component_router_link, {
                                          to: '/store/products/' + product.id
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(product.attributes.description), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["to"])
                                      ]))
                                    : _createCommentVNode("", true)
                                ]),
                                _: 2
                              }, 1032, ["columns"])
                            ]),
                            _: 2
                          }, 1024))
                        }), 128)),
                        (query.value && !searching.value && !searchResults.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                              _createVNode(_unref(QuestionMarkCircleIcon), { class: "mx-auto h-12 w-12 text-gray-400" }),
                              _cache[43] || (_cache[43] = _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-900" }, "No results", -1))
                            ]))
                          : (!view.value.length)
                            ? (_openBlock(), _createBlock(_component_EmptyState, {
                                key: 1,
                                class: "text-center pt-3 pb-4",
                                plural: "products"
                              }))
                            : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  (!query.value && !currentFolderId.value)
                    ? (_openBlock(), _createBlock(_component_NeuePagination, {
                        key: 0,
                        size: size.value,
                        from: 1 + page.value * pageSize,
                        to: (1 + page.value) * pageSize,
                        onPrev: prev,
                        onNext: next
                      }, null, 8, ["size", "from", "to"]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["loading"])
      ])
    ]),
    _: 3
  }))
}
}

}